import type { IPublicClientApplication } from "@azure/msal-browser";
import { apiDelete, apiFetch, apiPost, resolveBlobIdUrl } from "./client";
import { createImageUploadFormData } from "./uploadRequest";
import type { Paged, PillowDetail, PillowGalleryItem } from "../types/api";

type ApiVersion = { id: string; blobId: string; previewId: string | null; createdAt: string; supersededAt: string };

type ApiModel = {
  id: string;
  color: string;
  blobId: string;
  previewId: string | null;
  printId: string | null;
  previousVersions: ApiVersion[];
};

type ApiPillow = {
  id: string;
  models: ApiModel[];
  image: { blobId: string };
};

type ApiGalleryItem = {
  id: string;
  createdAt: string;
  previewBlobId: string | null;
  previewColor: string | null;
  imageBlobId: string;
};

export interface PillowVariant {
  color: string;
  removalMode: string;
}

export const DEFAULT_PILLOW_VARIANT: PillowVariant = { color: "#ffffff", removalMode: "rembg" };

const blobUrl = (id: string | null) => (id === null ? null : resolveBlobIdUrl(id));

function toPillow({ id, image, models }: ApiPillow): PillowDetail {
  return {
    id,
    image: { id: image.blobId, url: resolveBlobIdUrl(image.blobId) },
    models: models.map((m) => ({
      id: m.id,
      color: m.color,
      url: resolveBlobIdUrl(m.blobId),
      previewId: m.previewId,
      previewUrl: blobUrl(m.previewId),
      printId: m.printId,
      printUrl: blobUrl(m.printId),
      previousVersions: m.previousVersions.map((v) => ({
        id: v.id,
        url: resolveBlobIdUrl(v.blobId),
        previewUrl: blobUrl(v.previewId),
        createdAt: v.createdAt,
        supersededAt: v.supersededAt,
      })),
    })),
  };
}

export function fetchPillows(
  msal: IPublicClientApplication,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Paged<PillowGalleryItem>> {
  return apiFetch<Paged<ApiGalleryItem>>(`/web/pillow?page=${page}&pageSize=${pageSize}`, msal, { signal }).then(
    ({ items, ...rest }) => ({
      ...rest,
      items: items.map((item) => ({
        id: item.id,
        createdAt: item.createdAt,
        previewColor: item.previewColor,
        previewUrl: blobUrl(item.previewBlobId),
        imageUrl: resolveBlobIdUrl(item.imageBlobId),
      })),
    }),
  );
}

export function fetchPillow(msal: IPublicClientApplication, id: string, signal?: AbortSignal): Promise<PillowDetail> {
  return apiFetch<ApiPillow>(`/web/pillow/${id}`, msal, { signal }).then(toPillow);
}

export async function generatePillow(
  msal: IPublicClientApplication,
  image: File,
  variant: PillowVariant,
  forceGenerate: boolean,
): Promise<PillowDetail> {
  const formData = await createImageUploadFormData(image, { ...variant, forceGenerate });
  return toPillow(await apiFetch<ApiPillow>("/web/pillow/generate", msal, { method: "POST", body: formData }));
}

/** (Re)generate one colour for an existing pillow from the original it already holds. */
export function regeneratePillow(msal: IPublicClientApplication, id: string, variant: PillowVariant): Promise<PillowDetail> {
  return apiPost<ApiPillow>(`/web/pillow/${id}/generate`, msal, variant).then(toPillow);
}

export function deletePillow(msal: IPublicClientApplication, id: string): Promise<void> {
  return apiDelete(`/web/pillow/${id}`, msal);
}
